import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';
import { DEFAULT_STORE_ID } from '../firebase/firebase';

// Default empty summary to avoid duplication
const EMPTY_SUMMARY = {
    subtotal: 0,
    shipping: 0,
    tax: 0,
    total: 0,
    taxIncluded: false,
    qualifiesForFreeDelivery: false,
    minimumSpendForFreeDelivery: null
};

export const useOrderCalculations = (cart, storeSettings, currentUser) => {
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [promoLoading, setPromoLoading] = useState(false);
    const [promoError, setPromoError] = useState("");
    const [calculationLoading, setCalculationLoading] = useState(false);
    const [validatedItems, setValidatedItems] = useState(null); // Add state for enriched items

    // Server-side calculation function for accurate totals
    const calculateSummary = useCallback(async (cartData = cart, selectedShippingMethod = "", promoCode = undefined) => {
        if (!cartData || !cartData.items || cartData.items.length === 0) {
            setSummary(EMPTY_SUMMARY);
            return EMPTY_SUMMARY;
        }

        setCalculationLoading(true);

        try {
            // Preserve existing promo code if not explicitly changing it
            const effectivePromoCode = promoCode !== undefined ? promoCode : appliedPromo;

            // SECURITY: Only send minimal data - server will fetch product details
            // This prevents price manipulation and reduces payload size
            const sanitizedCartData = {
                items: cartData.items.map(item => ({
                    productId: item.productId,
                    variantId: item.variantId || null,
                    quantity: item.quantity
                    // Server will fetch: price, name, image, stock, etc. from database
                }))
            };

            // Call server-side calculation function for accurate totals
            const calculateOrderSummaryFunction = httpsCallable(functions, 'calculateOrderSummary');
            const result = await calculateOrderSummaryFunction({
                cartData: sanitizedCartData,
                storeId: DEFAULT_STORE_ID,
                shippingMethodId: selectedShippingMethod,
                promoCode: effectivePromoCode?.code || null
            });

            if (result.data.success) {
                const newSummary = {
                    subtotal: result.data.summary.subtotal || 0,
                    shipping: result.data.summary.shipping || 0,
                    tax: result.data.summary.tax || 0,
                    total: result.data.summary.total || 0,
                    taxIncluded: result.data.summary.taxIncluded || false,
                    qualifiesForFreeDelivery: result.data.summary.qualifiesForFreeDelivery || false,
                    minimumSpendForFreeDelivery: result.data.summary.minimumSpendForFreeDelivery || null,
                    currency: result.data.summary.currency || 'AED'
                };

                setSummary(newSummary);

                // Store validated items with enriched pricing
                if (result.data.validatedItems) {
                    setValidatedItems(result.data.validatedItems);
                }

                // Update applied promo if server validated one
                if (result.data.appliedPromo) {
                    setAppliedPromo(result.data.appliedPromo);
                    setDiscountAmount(result.data.summary.discount || 0);
                    setPromoError(""); // Clear any previous errors
                } else if (result.data.promoError) {
                    // Server returned a promo error (like minimum order not met)
                    setAppliedPromo(null);
                    setDiscountAmount(0);
                    setPromoError(result.data.promoError);
                } else if (promoCode === null) {
                    // Explicitly clearing promo
                    setAppliedPromo(null);
                    setDiscountAmount(0);
                    setPromoError("");
                }

                setCalculationLoading(false);
                return newSummary;
            } else {
                throw new Error(result.data.message || 'Calculation failed');
            }

        } catch (error) {
            console.error('Error calculating order summary:', error);
            toast.error('Error calculating totals. Please refresh and try again.');

            // Fallback to empty summary
            setSummary(EMPTY_SUMMARY);
            setCalculationLoading(false);
            return EMPTY_SUMMARY;
        }
    }, [cart, appliedPromo]);

    // Simplified promo code application - server-side validation only
    const applyDiscount = async (promoCode, currentShippingMethod = "") => {
        if (!promoCode.trim()) {
            setPromoError("Please enter a promo code");
            return false;
        }

        if (!cart || !cart.items || cart.items.length === 0) {
            setPromoError("Cannot apply promo code to empty cart");
            return false;
        }

        setPromoLoading(true);
        setPromoError("");

        try {
            // Let the server handle all validation by applying the promo code directly
            // This will validate the promo code and calculate the discount on the server
            await calculateSummary(cart, currentShippingMethod, { code: promoCode });

            // Check if the server accepted the promo code
            if (appliedPromo && appliedPromo.code === promoCode) {
                toast.success("Promo code applied successfully!");
                return true;
            } else {
                // If there's an error from server, it will be in promoError
                // toast.error(promoError || "Invalid promo code");
                return false;
            }

        } catch (error) {
            console.error("Error applying promo code:", error);
            setPromoError("Failed to apply promo code. Please try again.");
            toast.error("Failed to apply promo code. Please try again.");
            return false;
        } finally {
            setPromoLoading(false);
        }
    };

    const removeDiscount = useCallback(async (currentShippingMethod = "") => {
        setAppliedPromo(null);
        setDiscountAmount(0);
        setPromoError("");
        // Force immediate recalculation without discount using server-side calculation
        await calculateSummary(cart, currentShippingMethod, null);

    }, [cart, calculateSummary]);



    return {
        summary,
        discountAmount,
        appliedPromo,
        promoLoading,
        promoError,
        calculationLoading,
        validatedItems, // Add validated items to exports
        calculateSummary,
        applyDiscount,
        removeDiscount,
        // Add setDiscountAmount to exports for external updates
        setDiscountAmount,
        // Utility function to get formatted currency amount
        formatCurrency: (amount) => {
            const currency = storeSettings?.currency || 'AED';
            return `${currency} ${parseFloat(amount).toFixed(2)}`;
        }
    };
};